import React, { useState } from 'react';
import clsx from 'clsx';
import { Link, Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import PageHeader from '../../components/ui/PageHeader';
import UiCard from '../../components/ui/UiCard';
import FilterSelect from '../../components/ui/FilterSelect';
import FormPageLayout from '../../components/ui/FormPageLayout';
import { FORMA_LINK_BTN } from '../../utils/listPageUi';
import { useLanguage } from '../../contexts/LanguageContext';
import { formationAPI, spaEntityAPI } from '../../services/api';
import { queryKeys } from '../../utils/queryKeys';
import { invalidateFormationGraph } from '../../utils/invalidateFormationGraph';
import {
  ClipboardDocumentCheckIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  PencilSquareIcon,
  ChartBarIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  ArrowsPointingOutIcon,
  StarIcon,
  UserIcon,
  BookOpenIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { confirmDialog } from '../../utils/confirmBridge';

const EvaluationsPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterModule, setFilterModule] = useState('all');
  
  // État pour le formulaire d'évaluation
  const [formData, setFormData] = useState({
    studentId: '',
    studentName: '',
    moduleId: '',
    moduleName: '',
    evaluationType: 'exam',
    evaluationDate: '',
    score: '',
    maxScore: '100',
    instructorId: '',
    instructorName: '',
    comments: '',
    strengths: '',
    weaknesses: '',
    recommendations: ''
  });

  const { data: modules = [] } = useQuery(queryKeys.spa.courses, async () => {
    const courses = await spaEntityAPI.list('courses');
    return (courses || []).map((course) => ({
      id: course.id,
      name: course.title || course.name,
    }));
  }, { staleTime: 15_000, refetchOnWindowFocus: true });

  const { data: students = [] } = useQuery(queryKeys.formation.studentsCatalog, async () => {
    const res = await formationAPI.getStudents({ limit: 500, page: 1 });
    return (res?.items || []).map((student) => ({
      id: student.id,
      name: student.name || `${student.firstName || ''} ${student.lastName || ''}`.trim(),
      studentCode: student.studentCode || student.code || '',
    }));
  }, { staleTime: 15_000, refetchOnWindowFocus: true });

  const { data: trainers = [] } = useQuery(queryKeys.spa.trainers, async () => {
    const trainersList = await spaEntityAPI.list('trainers');
    return (trainersList || []).map((trainer) => ({
      id: trainer.id,
      name: trainer.name || `${trainer.firstName || ''} ${trainer.lastName || ''}`.trim(),
      specialty: trainer.specialty || trainer.specialization || '',
    }));
  }, { staleTime: 15_000, refetchOnWindowFocus: true });

  const FORMATION_EVALUATIONS_QUERY_KEY = queryKeys.formation.evaluations;

  const { data: evaluationsData, isLoading } = useQuery(FORMATION_EVALUATIONS_QUERY_KEY, async () => {
    try {
      const res = await formationAPI.getEvaluations();
      const items = Array.isArray(res?.items) ? res.items : [];
      return {
        evaluations: items.map((e) => ({
          ...e,
          score: e.score != null ? Number(e.score) : null,
          maxScore: e.maxScore != null ? Number(e.maxScore) : null,
          status:
            e.status ?? (e.score != null && e.score !== '' ? 'completed' : 'pending'),
        })),
      };
    } catch (error) {
      console.error('Error fetching evaluations:', error);
      return { evaluations: [] };
    }
  });

  function newFormationEvaluationId() {
    return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? `eval-${crypto.randomUUID()}`
      : `eval-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
  }

  const addEvaluationMutation = useMutation(
    async (form) => {
      const score = form.score === '' || form.score == null ? null : Number(form.score);
      const maxScoreRaw = form.maxScore === '' || form.maxScore == null ? 100 : Number(form.maxScore);
      const maxScore = Number.isFinite(maxScoreRaw) ? maxScoreRaw : 100;
      const payload = {
        id: newFormationEvaluationId(),
        studentId: form.studentId ?? '',
        studentName: form.studentName ?? '',
        moduleId: form.moduleId ?? '',
        moduleName: form.moduleName ?? '',
        evaluationType: form.evaluationType ?? 'exam',
        evaluationDate:
          form.evaluationDate ||
          new Date().toISOString().slice(0, 10),
        score,
        maxScore,
        instructorId: form.instructorId ?? '',
        instructorName: form.instructorName ?? '',
        comments: form.comments ?? '',
        strengths: form.strengths ?? '',
        weaknesses: form.weaknesses ?? '',
        recommendations: form.recommendations ?? '',
      };
      const created = await formationAPI.createEvaluation(payload);
      return {
        evaluation: created,
        message: 'Évaluation ajoutée avec succès',
      };
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(FORMATION_EVALUATIONS_QUERY_KEY);
        await invalidateFormationGraph(queryClient);
        resetForm();
        navigate('/evaluations');
      },
    }
  );

  const deleteEvaluationMutation = useMutation(
    async (evaluationId) => {
      await formationAPI.deleteEvaluation(evaluationId);
      return { message: 'Évaluation supprimée avec succès' };
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(FORMATION_EVALUATIONS_QUERY_KEY);
        await invalidateFormationGraph(queryClient);
      },
    }
  );

  // Gestion du formulaire
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Mettre à jour automatiquement le nom du module
    if (name === 'moduleId') {
      const selectedModule = modules.find(m => m.id === value);
      if (selectedModule) {
        setFormData(prev => ({
          ...prev,
          moduleName: selectedModule.name
        }));
      }
    }
  };

  // Réinitialiser le formulaire
  const resetForm = () => {
    setFormData({
      studentId: '',
      studentName: '',
      moduleId: '',
      moduleName: '',
      evaluationType: 'exam',
      evaluationDate: '',
      score: '',
      maxScore: '100',
      instructorId: '',
      instructorName: '',
      comments: '',
      strengths: '',
      weaknesses: '',
      recommendations: ''
    });
  };

  // Gérer la soumission du formulaire
  const handleSubmit = (e) => {
    e.preventDefault();
    addEvaluationMutation.mutate(formData);
  };

  // Afficher les détails d'une évaluation
  const handleViewDetails = (evaluation) => {
    navigate(`/evaluations/${evaluation.id}`);
  };

  // Confirmer la suppression
  const handleDelete = async (evaluationId) => {
    if (await confirmDialog(t('evaluationsUi.confirmDeleteEvaluation'))) {
      deleteEvaluationMutation.mutate(evaluationId);
    }
  };

  // Calculer la note en étoiles (sur 5)
  const calculateStars = (score, maxScore = 100) => {
    if (!score) return 0;
    return Math.round((score / maxScore) * 5);
  };

  // Filtrer les évaluations
  const filteredEvaluations = evaluationsData?.evaluations
    ? evaluationsData.evaluations.filter(evaluation => {
        const matchesSearch = 
          evaluation.studentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          evaluation.moduleName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          evaluation.instructorName?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesModule = filterModule === 'all' || evaluation.moduleId === filterModule;
        
        return matchesSearch && matchesModule;
      })
    : [];

  const evaluationForm = (
    <UiCard>
      <h2 className="mb-6 text-xl font-bold text-slate-900 dark:text-slate-50">
        {t('evaluationsUi.newEvaluationBtn')}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm text-slate-600 dark:text-slate-400">
              {t('studentsPage.pageTitle') || 'Étudiant'}
            </label>
            <select
              name="studentId"
              value={formData.studentId}
              onChange={(e) => {
                const selectedStudent = students.find((s) => s.id === e.target.value);
                setFormData((prev) => ({
                  ...prev,
                  studentId: e.target.value,
                  studentName: selectedStudent ? selectedStudent.name : '',
                }));
              }}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-800/80 dark:text-slate-50"
              required
              disabled={students.length === 0}
            >
              <option value="">
                {students.length === 0
                  ? t('studentsPage.loading') || 'Chargement des étudiants...'
                  : t('evaluationsUi.selectStudent') || 'Sélectionner un étudiant'}
              </option>
              {students.map((student) => (
                <option key={student.id} value={student.id}>
                  {student.name} {student.studentCode ? `(${student.studentCode})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm text-slate-600 dark:text-slate-400">
              {t('evaluationsUi.moduleLabel') || 'Module'}
            </label>
            <select
              name="moduleId"
              value={formData.moduleId}
              onChange={(e) => {
                const selectedModule = modules.find((m) => m.id === e.target.value);
                setFormData((prev) => ({
                  ...prev,
                  moduleId: e.target.value,
                  moduleName: selectedModule ? selectedModule.name : '',
                }));
              }}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-800/80 dark:text-slate-50"
              required
              disabled={modules.length === 0}
            >
              <option value="">
                {modules.length === 0
                  ? t('coursesPage.loading') || 'Chargement des modules...'
                  : t('evaluationsUi.selectModule') || 'Sélectionner un module'}
              </option>
              {modules.map((module) => (
                <option key={module.id} value={module.id}>
                  {module.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm text-slate-600 dark:text-slate-400">Type</label>
            <select
              name="evaluationType"
              value={formData.evaluationType}
              onChange={handleFormChange}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-800/80 dark:text-slate-50"
              required
            >
              <option value="exam">{t('evaluationsUi.typeExam')}</option>
              <option value="project">{t('evaluationsUi.typeProject')}</option>
              <option value="quiz">{t('evaluationsUi.typeQuiz')}</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm text-slate-600 dark:text-slate-400">
              {t('evaluationsUi.evalDateLabel')}
            </label>
            <input
              type="date"
              name="evaluationDate"
              value={formData.evaluationDate}
              onChange={handleFormChange}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-800/80 dark:text-slate-50"
            />
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="mb-1 block text-sm text-slate-600 dark:text-slate-400">
                {t('certificationsComponents.scoreFieldLbl') || 'Score'}
              </label>
              <input
                type="number"
                name="score"
                value={formData.score}
                onChange={handleFormChange}
                min="0"
                max={formData.maxScore}
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-800/80 dark:text-slate-50"
              />
            </div>
            <div className="w-24">
              <label className="mb-1 block text-sm text-slate-600 dark:text-slate-400">
                {t('evaluationsUi.scoreOutOfLabel')}
              </label>
              <input
                type="number"
                name="maxScore"
                value={formData.maxScore}
                onChange={handleFormChange}
                min="1"
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-800/80 dark:text-slate-50"
                required
              />
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm text-slate-600 dark:text-slate-400">
              {t('trainersPage.pageTitle') || 'Formateur'}
            </label>
            <select
              name="instructorId"
              value={formData.instructorId}
              onChange={(e) => {
                const selectedTrainer = trainers.find((tr) => tr.id === e.target.value);
                setFormData((prev) => ({
                  ...prev,
                  instructorId: e.target.value,
                  instructorName: selectedTrainer ? selectedTrainer.name : '',
                }));
              }}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-800/80 dark:text-slate-50"
              required
              disabled={trainers.length === 0}
            >
              <option value="">
                {trainers.length === 0
                  ? t('trainersPage.loading') || 'Chargement...'
                  : t('evaluationsUi.selectTrainer') || 'Sélectionner un formateur'}
              </option>
              {trainers.map((trainer) => (
                <option key={trainer.id} value={trainer.id}>
                  {trainer.name} {trainer.specialty ? `- ${trainer.specialty}` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="mb-1 block text-sm text-slate-600 dark:text-slate-400">
            {t('evaluationsUi.commentsLabel') || 'Commentaires'}
          </label>
          <textarea
            name="comments"
            value={formData.comments}
            onChange={handleFormChange}
            rows="3"
            className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-800/80 dark:text-slate-50"
          />
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm text-slate-600 dark:text-slate-400">
              {t('evaluationsUi.strengthsLabel')}
            </label>
            <textarea
              name="strengths"
              value={formData.strengths}
              onChange={handleFormChange}
              rows="2"
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-800/80 dark:text-slate-50"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-slate-600 dark:text-slate-400">
              {t('evaluationsUi.improvementsLabel')}
            </label>
            <textarea
              name="weaknesses"
              value={formData.weaknesses}
              onChange={handleFormChange}
              rows="2"
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-800/80 dark:text-slate-50"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-slate-600 dark:text-slate-400">
              {t('evaluationsUi.recommendationsLabel')}
            </label>
            <textarea
              name="recommendations"
              value={formData.recommendations}
              onChange={handleFormChange}
              rows="2"
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-800/80 dark:text-slate-50"
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 border-t border-slate-200 pt-4 dark:border-slate-600">
          <button
            type="button"
            onClick={() => navigate('/evaluations')}
            className="rounded-lg bg-slate-50 px-4 py-2 text-slate-900 transition-colors hover:bg-slate-100 dark:bg-slate-800/80 dark:text-slate-50 dark:hover:bg-slate-800"
          >
            {t('common.cancelBtn')}
          </button>
          <button type="submit" className={clsx(FORMA_LINK_BTN, 'px-6 py-2')}>
            {t('evaluationsUi.newEvaluationBtn')}
          </button>
        </div>
      </form>
    </UiCard>
  );

  return (
    <div className="container mx-auto space-y-6 px-4 py-6">
      <Routes>
        <Route
          path="add"
          element={
            <FormPageLayout backTo="/evaluations" backLabel={t('studentsPage.backToList')}>
              {evaluationForm}
            </FormPageLayout>
          }
        />
        <Route
          path=":id"
          element={
            <EvaluationDetailRoute
              evaluations={evaluationsData?.evaluations}
              calculateStars={calculateStars}
            />
          }
        />
        <Route
          index
          element={
            <>
      <PageHeader
        title={t('evaluationsPageTitle')}
        description={t('pageDescriptions.evaluations')}
        actions={
          <Link to="/evaluations/add" className={FORMA_LINK_BTN}>
            <PlusIcon className="h-5 w-5" />
            <span>{t('evaluationsUi.newEvaluationBtn')}</span>
          </Link>
        }
      />

      <UiCard>
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="relative min-w-0 flex-1 sm:min-w-[14rem]">
            <label htmlFor="evaluation-search" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {t('evaluationsUi.searchPlaceholder')}
            </label>
            <MagnifyingGlassIcon className="pointer-events-none absolute bottom-2.5 h-5 w-5 text-slate-400 dark:text-slate-500 ltr:left-3 rtl:right-3" />
            <input
              id="evaluation-search"
              type="search"
              placeholder={t('evaluationsUi.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-forma-500 focus:outline-none focus:ring-2 focus:ring-forma-500/25 ltr:pl-10 rtl:pr-10 ltr:pr-4 rtl:pl-4 dark:border-slate-600 dark:bg-slate-950 dark:text-white dark:placeholder:text-slate-500"
            />
          </div>
          <FilterSelect
            id="evaluation-module-filter"
            label={t('evaluationsUi.moduleLabel')}
            value={filterModule}
            onChange={setFilterModule}
            options={[
              { id: 'all', name: t('evaluationsUi.allModules') },
              ...modules.map((module) => ({ id: module.id, name: module.name })),
            ]}
            className="sm:min-w-[11rem] md:min-w-[14rem]"
          />
        </div>
      </UiCard>
      
      {/* Liste des évaluations */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
      ) : filteredEvaluations.length === 0 ? (
        <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-8 text-center">
          <ClipboardDocumentCheckIcon className="h-16 w-16 text-slate-900 dark:text-slate-50/30 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-2">
            {t('evaluationsUi.emptyTitle')}
          </h3>
          <p className="text-slate-600 dark:text-slate-400">
            {t('evaluationsUi.emptyBody')}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEvaluations.map(evaluation => {
            const stars = calculateStars(
              Number(evaluation.score),
              Math.max(Number(evaluation.maxScore) || 100, 1)
            );
            
            return (
              <motion.div
                key={evaluation.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-5"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                      evaluation.evaluationType === 'exam' 
                        ? 'bg-forma-500/15 text-forma-700 dark:text-forma-300' 
                        : evaluation.evaluationType === 'project' 
                          ? 'bg-green-500/20 text-green-300' 
                          : 'bg-amber-500/20 text-amber-300'
                    }`}>
                      {evaluation.evaluationType === 'exam'
                        ? t('evaluationsUi.typeExam')
                        : evaluation.evaluationType === 'project'
                          ? t('evaluationsUi.typeProject')
                          : t('evaluationsUi.typeQuiz')}
                    </span>
                    
                    {evaluation.status === 'pending' && (
                      <span className="ms-2 bg-gray-500/20 text-gray-300 text-xs px-2 py-1 rounded-full">
                        {t('evaluationsUi.statusPending')}
                      </span>
                    )}
                  </div>
                  
                  <div className="flex">
                    <button
                      onClick={() => handleViewDetails(evaluation)}
                      className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800/80 rounded-lg transition-colors"
                      title={t('commonActions.detailsSr')}
                    >
                      <ArrowsPointingOutIcon className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                    </button>
                    
                    <button
                      onClick={() => handleDelete(evaluation.id)}
                      className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800/80 rounded-lg transition-colors ms-1"
                      title={t('commonActions.delete')}
                    >
                      <TrashIcon className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                    </button>
                  </div>
                </div>
                
                <h3 className="text-slate-900 dark:text-slate-50 font-semibold mt-2 line-clamp-1">
                  {evaluation.moduleName}
                </h3>
                
                <div className="flex items-center mt-2">
                  <UserIcon className="h-4 w-4 text-slate-600 dark:text-slate-400 mr-2" />
                  <span className="text-slate-700 dark:text-slate-300 text-sm">{evaluation.studentName}</span>
                </div>
                
                {evaluation.evaluationDate && (
                  <div className="flex items-center mt-1">
                    <CalendarDaysIcon className="h-4 w-4 text-slate-600 dark:text-slate-400 mr-2" />
                    <span className="text-slate-600 dark:text-slate-400 text-xs">
                      {new Date(evaluation.evaluationDate).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                )}
                
                {evaluation.score != null &&
                  evaluation.score !== '' &&
                  !Number.isNaN(Number(evaluation.score)) && (
                  <div className="mt-3">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="w-5 h-5">
                          {i < stars ? (
                            <StarIconSolid className="h-5 w-5 text-yellow-400" />
                          ) : (
                            <StarIcon className="h-5 w-5 text-slate-900 dark:text-slate-50/30" />
                          )}
                        </div>
                      ))}
                      <span className="ml-2 text-slate-900 dark:text-slate-50 font-semibold">
                        {evaluation.score}/{evaluation.maxScore ?? 100}
                      </span>
                    </div>
                    
                    <div className="w-full bg-slate-50 dark:bg-slate-800/80 h-2 rounded-full mt-2">
                      <div
                        className="h-2 rounded-full bg-gradient-to-r from-forma-500 to-forma-600"
                        style={{
                          width: `${Math.min(
                            (Number(evaluation.score) /
                              Math.max(Number(evaluation.maxScore) || 100, 1)) *
                              100,
                            100
                          )}%`,
                        }}
                  ></div>
                </div>
              </div>
            )}

                <div className="mt-3 text-slate-600 dark:text-slate-400 text-sm line-clamp-2">
                  {evaluation.comments || t('evaluationsUi.noCommentLabel')}
                </div>

                <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-600 flex justify-between">
                  <div className="text-slate-600 dark:text-slate-400 text-xs">
                    {t('evaluationsUi.evaluatedByLabel')}: {evaluation.instructorName}
                  </div>

                  <button
                    onClick={() => handleViewDetails(evaluation)}
                    className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
                  >
                    {t('evaluationsUi.detailsBtn')}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
            </>
          }
        />
      </Routes>
    </div>
  );
};

const EvaluationDetailRoute = ({ evaluations, calculateStars }) => {
  const { t } = useLanguage();
  const { id } = useParams();
  const evaluation = evaluations?.find((e) => e.id === id);

  if (!evaluation) {
    return (
      <FormPageLayout backTo="/evaluations" backLabel={t('studentsPage.backToList')}>
        <p className="text-slate-600 dark:text-slate-400">{t('common.noEvaluationFound')}</p>
      </FormPageLayout>
    );
  }

  const typeLabel =
    evaluation.evaluationType === 'exam'
      ? 'Examen'
      : evaluation.evaluationType === 'project'
        ? 'Projet'
        : 'Quiz';

  const panelClass =
    'rounded-xl border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-600 dark:bg-slate-800/40';

  return (
    <FormPageLayout backTo="/evaluations" backLabel={t('studentsPage.backToList')}>
      <UiCard>
        <div className="mb-6">
          <span
            className={clsx(
              'inline-block px-2 py-1 text-xs rounded-full mb-2',
              evaluation.evaluationType === 'exam'
                ? 'bg-forma-500/15 text-forma-700 dark:text-forma-300'
                : evaluation.evaluationType === 'project'
                  ? 'bg-green-500/20 text-green-300'
                  : 'bg-amber-500/20 text-amber-300'
            )}
          >
            {typeLabel}
          </span>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50">
            {evaluation.moduleName}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className={clsx(panelClass, 'flex items-center gap-3')}>
            <UserIcon className="h-5 w-5 text-slate-500 dark:text-slate-400" />
            <div>
              <p className="text-slate-600 dark:text-slate-400 text-xs">{t('evaluationsUi.studentLabel')}</p>
              <p className="text-slate-900 dark:text-slate-50">{evaluation.studentName}</p>
            </div>
          </div>
          <div className={clsx(panelClass, 'flex items-center gap-3')}>
            <BookOpenIcon className="h-5 w-5 text-slate-500 dark:text-slate-400" />
            <div>
              <p className="text-slate-600 dark:text-slate-400 text-xs">{t('evaluationsUi.moduleLabel')}</p>
              <p className="text-slate-900 dark:text-slate-50">{evaluation.moduleName}</p>
            </div>
          </div>
          <div className={clsx(panelClass, 'flex items-center gap-3')}>
            <CalendarDaysIcon className="h-5 w-5 text-slate-500 dark:text-slate-400" />
            <div>
              <p className="text-slate-600 dark:text-slate-400 text-xs">{t('evaluationsUi.dateLabel')}</p>
              <p className="text-slate-900 dark:text-slate-50">
                {evaluation.evaluationDate
                  ? new Date(evaluation.evaluationDate).toLocaleDateString('fr-FR')
                  : t('classroomsPage.notSpecifiedFloor')}
              </p>
            </div>
          </div>
        </div>

        {evaluation.score ? (
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <p className="text-slate-700 dark:text-slate-300 font-medium">{t('evaluationsUi.scoreLabel')}</p>
              <p className="text-slate-900 dark:text-slate-50 font-bold text-2xl">
                {evaluation.score}/{evaluation.maxScore}
              </p>
            </div>
            <div className="w-full bg-slate-50 dark:bg-slate-800/80 h-3 rounded-full mt-2">
              <div
                className="h-3 rounded-full bg-gradient-to-r from-forma-500 to-forma-600"
                style={{ width: `${(evaluation.score / evaluation.maxScore) * 100}%` }}
              />
            </div>
            <div className="flex justify-center mt-3">
              {[...Array(5)].map((_, i) => {
                const stars = calculateStars(evaluation.score, evaluation.maxScore);
                return (
                  <div key={i} className="mx-1">
                    {i < stars ? (
                      <StarIconSolid className="h-6 w-6 text-yellow-400" />
                    ) : (
                      <StarIcon className="h-6 w-6 text-slate-900 dark:text-slate-50/30" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="mb-6 bg-amber-500/20 text-amber-300 p-4 rounded-xl text-center">
            {t('evaluationsUi.pendingScore') || 'Évaluation en attente de note'}
          </div>
        )}

        <div className={clsx(panelClass, 'mb-6')}>
          <h3 className="text-slate-900 dark:text-slate-50 font-medium mb-2">{t('evaluationsUi.commentsSection')}</h3>
          <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
            {evaluation.comments || t('evaluationsUi.noCommentLabel')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className={panelClass}>
            <h3 className="text-green-400 font-medium mb-2 flex items-center">
              <ChartBarIcon className="h-4 w-4 mr-1" /> {t('evaluationsUi.strengthsLabel')}
            </h3>
            <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
              {evaluation.strengths || t('coursesPage.trainerSpecialityMissing')}
            </p>
          </div>
          <div className={panelClass}>
            <h3 className="text-amber-400 font-medium mb-2 flex items-center">
              <ChartBarIcon className="h-4 w-4 mr-1" /> {t('evaluationsUi.improvementsLabel')}
            </h3>
            <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
              {evaluation.weaknesses || t('coursesPage.trainerSpecialityMissing')}
            </p>
          </div>
          <div className={panelClass}>
            <h3 className="text-blue-400 font-medium mb-2 flex items-center">
              <ChartBarIcon className="h-4 w-4 mr-1" /> {t('evaluationsUi.recommendationsLabel')}
            </h3>
            <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
              {evaluation.recommendations || t('coursesPage.trainerSpecialityMissing')}
            </p>
          </div>
        </div>

        <p className="text-slate-600 dark:text-slate-400 text-sm pt-4 border-t border-slate-200 dark:border-slate-600">
          {t('evaluationsUi.evaluatedByLabel')}: {evaluation.instructorName}
        </p>
      </UiCard>
    </FormPageLayout>
  );
};

export default EvaluationsPage;
